import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DevToolsService } from '../dev-tools.service';
import {
  DevToolsEvent,
  DevToolsEventLevel,
  DevToolsEventSource,
} from '../dev-tools.types';
import { DevToolsWsInterceptorService } from '../ws-interceptor.service';

const ALL_SOURCES: DevToolsEventSource[] = [
  'task-ws',
  'payment-ws',
  'notif-ws',
  'action',
  'http',
  'custom',
];
const ALL_LEVELS: DevToolsEventLevel[] = ['info', 'success', 'warn', 'error'];

@Component({
  selector: 'app-dev-tools-ws-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        color: #f5f5f5;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .row:hover {
        background: rgba(148, 163, 184, 0.08);
      }
      pre {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 11px;
        line-height: 1.45;
        white-space: pre-wrap;
        word-break: break-word;
      }
      .scroll-y {
        overflow-y: auto;
        scrollbar-width: thin;
      }
      .scroll-y::-webkit-scrollbar {
        width: 8px;
      }
      .scroll-y::-webkit-scrollbar-thumb {
        background: rgba(100, 116, 139, 0.5);
        border-radius: 4px;
      }
    `,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-gray-200 tw-text-xs">
      <!-- Stats bar -->
      <div class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/40 tw-flex-wrap">
        <div class="tw-flex tw-items-center tw-gap-1">
          <i class="ph ph-pulse tw-text-success-500"></i>
          <span class="tw-font-semibold">{{ events().length }}</span>
          <span class="tw-text-gray-400">events</span>
        </div>
        <div class="tw-h-4 tw-w-px tw-bg-gray-700"></div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <span class="chip tw-bg-info-500/20 tw-text-info-500">task {{ counts().taskWs }}</span>
          <span class="chip tw-bg-gov-secondary/20 tw-text-gov-secondary">pay {{ counts().paymentWs }}</span>
          <span class="chip tw-bg-error-500/20 tw-text-error-500">notif {{ counts().notifWs }}</span>
          <span class="chip tw-bg-gray-500/20 tw-text-gray-300">autres {{ counts().other }}</span>
        </div>
        <div class="tw-h-4 tw-w-px tw-bg-gray-700"></div>
        <div class="tw-flex tw-items-center tw-gap-1">
          <span class="chip tw-bg-info-500/20 tw-text-info-500">i {{ counts().info }}</span>
          <span class="chip tw-bg-success-500/20 tw-text-success-500">✓ {{ counts().success }}</span>
          <span class="chip tw-bg-warning-500/20 tw-text-warning-500">! {{ counts().warn }}</span>
          <span class="chip tw-bg-error-500/20 tw-text-error-500">✗ {{ counts().error }}</span>
        </div>
        <div class="tw-h-4 tw-w-px tw-bg-gray-700"></div>
        <div class="tw-flex tw-items-center tw-gap-1 tw-text-gray-400">
          <i class="ph ph-lightning"></i>
          <span class="tw-font-semibold tw-text-gray-200">{{ eventsPerSecond() }}</span>
          <span>ev/s</span>
        </div>
        <div class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            (click)="togglePause()"
            class="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-transition"
            [ngClass]="paused() ? 'tw-bg-warning-500 tw-text-gray-900' : 'tw-bg-gray-700 tw-text-gray-200'"
          >
            <i class="ph" [ngClass]="paused() ? 'ph-play' : 'ph-pause'"></i>
            {{ paused() ? 'Reprendre' : 'Pause' }}
          </button>
          <button
            type="button"
            (click)="toggleAutoScroll()"
            class="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-transition"
            [ngClass]="autoScroll() ? 'tw-bg-success-500 tw-text-gray-900' : 'tw-bg-gray-700 tw-text-gray-200'"
          >
            <i class="ph ph-arrow-down"></i>
            Auto-scroll
          </button>
          <button
            type="button"
            (click)="exportNdjson()"
            class="tw-px-2 tw-py-1 tw-rounded tw-bg-gray-700 hover:tw-bg-gray-600 tw-text-gray-200 tw-text-xs tw-font-medium"
            title="Exporter les events filtrés en NDJSON"
          >
            <i class="ph ph-download-simple"></i>
            Export
          </button>
          <button
            type="button"
            (click)="clear()"
            class="tw-px-2 tw-py-1 tw-rounded tw-bg-error-500/80 hover:tw-bg-error-500 tw-text-white tw-text-xs tw-font-medium"
          >
            <i class="ph ph-trash"></i>
            Clear
          </button>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/20 tw-flex-wrap">
        <span class="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500">Source</span>
        <button
          *ngFor="let s of allSources"
          type="button"
          (click)="toggleSource(s)"
          class="chip tw-transition"
          [ngClass]="sourceFilter().has(s) ? 'tw-bg-success-500 tw-text-gray-900' : 'tw-bg-gray-800 tw-text-gray-400'"
        >
          {{ s }}
        </button>

        <span class="tw-ml-2 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500">Level</span>
        <button
          *ngFor="let l of allLevels"
          type="button"
          (click)="toggleLevel(l)"
          class="chip tw-transition"
          [ngClass]="levelFilter().has(l) ? 'tw-bg-success-500 tw-text-gray-900' : 'tw-bg-gray-800 tw-text-gray-400'"
        >
          {{ l }}
        </button>

        <input
          type="text"
          [ngModel]="search()"
          (ngModelChange)="search.set($event)"
          placeholder="Recherche type / demarcheRef…"
          class="tw-ml-auto tw-bg-gray-800 tw-text-gray-200 placeholder:tw-text-gray-500 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-1 tw-text-xs focus:tw-outline-none focus:tw-border-success-500 tw-w-56"
        />
        <input
          type="text"
          [ngModel]="highlightPattern()"
          (ngModelChange)="highlightPattern.set($event)"
          placeholder="Regex surlignage…"
          class="tw-bg-gray-800 tw-text-gray-200 placeholder:tw-text-gray-500 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-1 tw-text-xs focus:tw-outline-none focus:tw-border-warning-500 tw-w-40"
        />
      </div>

      <!-- Split view -->
      <div class="tw-flex tw-flex-1 tw-min-h-0">
        <!-- Timeline -->
        <div #timeline class="tw-flex-1 scroll-y tw-min-h-0">
          <div *ngIf="filteredEvents().length === 0" class="tw-p-6 tw-text-center tw-text-gray-500 tw-text-xs">
            Aucun event. Les Subjects WS sont écoutés — déclenche une action pour voir remonter les messages.
          </div>
          <div
            *ngFor="let ev of filteredEvents(); trackBy: trackEv"
            class="row tw-cursor-pointer tw-border-b tw-border-gray-800/60 tw-px-3 tw-py-1.5 tw-flex tw-items-start tw-gap-2"
            [ngClass]="{
              'tw-bg-alert-500/10': isHighlighted(ev),
              'tw-ring-1 tw-ring-success-500': selectedId() === ev.id
            }"
            (click)="select(ev.id)"
          >
            <span class="tw-text-gray-500 tw-font-mono tw-text-[10px] tw-whitespace-nowrap tw-pt-0.5"
              [title]="absoluteTime(ev.timestamp)">
              {{ formatTime(ev.timestamp) }}
            </span>
            <span class="chip" [ngClass]="sourceClass(ev.source)">{{ ev.source }}</span>
            <span class="tw-font-semibold tw-text-gray-200 tw-whitespace-nowrap">{{ ev.type }}</span>
            <span class="chip" [ngClass]="levelClass(ev.level)">{{ ev.level }}</span>
            <span *ngIf="ev.demarcheRef" class="chip tw-bg-gray-700/60 tw-text-gray-300">
              {{ ev.demarcheRef }}
            </span>
            <span class="tw-truncate tw-text-gray-500 tw-flex-1 tw-font-mono tw-text-[10px]">
              {{ preview(ev.payload) }}
            </span>
          </div>
        </div>

        <!-- Detail -->
        <div class="tw-w-[44%] tw-min-w-[280px] tw-max-w-[560px] tw-border-l tw-border-gray-700/60 tw-bg-gray-900/40 scroll-y tw-min-h-0">
          <ng-container *ngIf="selectedEvent() as sel; else noSel">
            <div class="tw-p-3 tw-space-y-2">
              <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span class="chip" [ngClass]="sourceClass(sel.source)">{{ sel.source }}</span>
                <span class="chip" [ngClass]="levelClass(sel.level)">{{ sel.level }}</span>
                <span class="tw-font-semibold tw-text-gray-100">{{ sel.type }}</span>
              </div>
              <div class="tw-text-[10px] tw-text-gray-400 tw-font-mono">
                {{ absoluteTime(sel.timestamp) }}
                <span class="tw-text-gray-500">({{ relativeTime(sel.timestamp) }})</span>
              </div>
              <div *ngIf="sel.demarcheRef" class="tw-text-[11px] tw-text-gray-300">
                <span class="tw-text-gray-500">demarcheRef : </span>
                <span class="tw-font-mono">{{ sel.demarcheRef }}</span>
              </div>
              <div class="tw-flex tw-items-center tw-gap-2 tw-pt-1">
                <button
                  type="button"
                  (click)="copyJson(sel)"
                  class="tw-px-2 tw-py-1 tw-rounded tw-bg-gray-700 hover:tw-bg-gray-600 tw-text-xs"
                >
                  <i class="ph ph-copy"></i> Copier JSON
                </button>
                <button
                  type="button"
                  (click)="reemit(sel)"
                  class="tw-px-2 tw-py-1 tw-rounded tw-bg-info-500 hover:tw-bg-info-700 tw-text-white tw-text-xs"
                  title="Re-pousser l'event dans le bus DevTools"
                >
                  <i class="ph ph-arrow-clockwise"></i> Re-emit
                </button>
                <span *ngIf="copyFeedback()" class="tw-text-success-500 tw-text-[10px]">
                  {{ copyFeedback() }}
                </span>
              </div>
              <pre
                class="tw-bg-gray-900/70 tw-border tw-border-gray-800 tw-rounded tw-p-2 tw-text-gray-200 tw-max-h-[60vh] scroll-y"
              >{{ serializedPayload() }}</pre>
            </div>
          </ng-container>
          <ng-template #noSel>
            <div class="tw-p-6 tw-text-center tw-text-gray-500 tw-text-xs">
              Sélectionne un event à gauche pour voir son payload complet.
            </div>
          </ng-template>
        </div>
      </div>
    </div>
  `,
})
export class DevToolsWsTabComponent implements OnInit {
  private readonly dev = inject(DevToolsService);
  private readonly interceptor = inject(DevToolsWsInterceptorService);

  readonly allSources = ALL_SOURCES;
  readonly allLevels = ALL_LEVELS;

  readonly events = this.dev.events;

  readonly paused = signal(false);
  readonly autoScroll = signal(true);
  readonly selectedId = signal<string | null>(null);
  readonly search = signal('');
  readonly highlightPattern = signal('');
  readonly sourceFilter = signal<Set<DevToolsEventSource>>(new Set(ALL_SOURCES));
  readonly levelFilter = signal<Set<DevToolsEventLevel>>(new Set(ALL_LEVELS));
  readonly copyFeedback = signal<string>('');

  // Frozen snapshot when paused
  private readonly frozenEvents = signal<DevToolsEvent[] | null>(null);

  // Live-updating tick used to refresh "ev/s" counter
  private readonly perSecTick = signal(0);

  @ViewChild('timeline') timelineRef?: ElementRef<HTMLDivElement>;

  readonly visibleEvents = computed(() => {
    const frozen = this.frozenEvents();
    return frozen ?? this.events();
  });

  readonly filteredEvents = computed(() => {
    const src = this.sourceFilter();
    const lvl = this.levelFilter();
    const term = this.search().trim().toLowerCase();
    return this.visibleEvents().filter((e) => {
      if (!src.has(e.source)) return false;
      if (!lvl.has(e.level)) return false;
      if (term) {
        const hay = `${e.type} ${e.demarcheRef ?? ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  });

  readonly counts = computed(() => {
    const evs = this.events();
    const c = { taskWs: 0, paymentWs: 0, notifWs: 0, other: 0, info: 0, success: 0, warn: 0, error: 0 };
    for (const e of evs) {
      if (e.source === 'task-ws') c.taskWs++;
      else if (e.source === 'payment-ws') c.paymentWs++;
      else if (e.source === 'notif-ws') c.notifWs++;
      else c.other++;
      c[e.level]++;
    }
    return c;
  });

  readonly eventsPerSecond = computed(() => {
    this.perSecTick();
    const now = Date.now();
    const since = now - 5000;
    const count = this.events().filter((e) => e.timestamp >= since).length;
    return Math.round((count / 5) * 10) / 10;
  });

  readonly selectedEvent = computed(() => {
    const id = this.selectedId();
    if (!id) return null;
    return this.events().find((e) => e.id === id) ?? null;
  });

  readonly serializedPayload = computed(() => {
    const sel = this.selectedEvent();
    if (!sel) return '';
    return this.dev.serialize(sel.payload);
  });

  constructor() {
    // Auto-scroll effect
    effect(() => {
      this.filteredEvents(); // track
      if (!this.autoScroll() || this.paused()) return;
      queueMicrotask(() => {
        const el = this.timelineRef?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });

    // Tick every second for ev/s
    if (typeof window !== 'undefined') {
      setInterval(() => this.perSecTick.update((v) => v + 1), 1000);
    }
  }

  ngOnInit(): void {
    this.interceptor.install();
  }

  togglePause(): void {
    const nowPaused = !this.paused();
    this.paused.set(nowPaused);
    if (nowPaused) {
      this.frozenEvents.set([...this.events()]);
    } else {
      this.frozenEvents.set(null);
    }
  }

  toggleAutoScroll(): void {
    this.autoScroll.update((v) => !v);
  }

  toggleSource(s: DevToolsEventSource): void {
    this.sourceFilter.update((set) => {
      const next = new Set(set);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  toggleLevel(l: DevToolsEventLevel): void {
    this.levelFilter.update((set) => {
      const next = new Set(set);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return next;
    });
  }

  clear(): void {
    this.dev.clearEvents();
    this.selectedId.set(null);
    this.frozenEvents.set(this.paused() ? [] : null);
  }

  select(id: string): void {
    this.selectedId.set(id);
  }

  trackEv = (_: number, e: DevToolsEvent) => e.id;

  formatTime(ts: number): string {
    const d = new Date(ts);
    const pad = (n: number, l = 2) => String(n).padStart(l, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
  }

  absoluteTime(ts: number): string {
    return new Date(ts).toISOString();
  }

  relativeTime(ts: number): string {
    const diff = Date.now() - ts;
    if (diff < 1000) return `il y a ${diff} ms`;
    if (diff < 60_000) return `il y a ${Math.round(diff / 1000)} s`;
    if (diff < 3_600_000) return `il y a ${Math.round(diff / 60_000)} min`;
    return `il y a ${Math.round(diff / 3_600_000)} h`;
  }

  preview(payload: unknown): string {
    if (payload === null || payload === undefined) return '';
    if (typeof payload !== 'object') return String(payload);
    try {
      const s = JSON.stringify(payload);
      return s.length > 140 ? s.slice(0, 140) + '…' : s;
    } catch {
      return '[unserialisable]';
    }
  }

  sourceClass(s: DevToolsEventSource): string {
    switch (s) {
      case 'task-ws':
        return 'tw-bg-info-500/20 tw-text-info-500';
      case 'payment-ws':
        return 'tw-bg-gov-secondary/20 tw-text-gov-secondary';
      case 'notif-ws':
        return 'tw-bg-error-500/20 tw-text-error-500';
      case 'action':
        return 'tw-bg-teal-primary/20 tw-text-teal-primary';
      case 'http':
        return 'tw-bg-info-500/20 tw-text-info-500';
      default:
        return 'tw-bg-gray-500/20 tw-text-gray-300';
    }
  }

  levelClass(l: DevToolsEventLevel): string {
    switch (l) {
      case 'success':
        return 'tw-bg-success-500/20 tw-text-success-500';
      case 'warn':
        return 'tw-bg-warning-500/20 tw-text-warning-500';
      case 'error':
        return 'tw-bg-error-500/20 tw-text-error-500';
      default:
        return 'tw-bg-info-500/20 tw-text-info-500';
    }
  }

  isHighlighted(ev: DevToolsEvent): boolean {
    const raw = this.highlightPattern().trim();
    if (!raw) return false;
    try {
      const re = new RegExp(raw, 'i');
      const hay = `${ev.type} ${ev.source} ${ev.demarcheRef ?? ''} ${this.preview(ev.payload)}`;
      return re.test(hay);
    } catch {
      return false;
    }
  }

  async copyJson(ev: DevToolsEvent): Promise<void> {
    try {
      const json = this.dev.serialize(ev.payload);
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(json);
      }
      this.copyFeedback.set('Copié !');
      setTimeout(() => this.copyFeedback.set(''), 1500);
    } catch {
      this.copyFeedback.set('Echec copie');
      setTimeout(() => this.copyFeedback.set(''), 1500);
    }
  }

  reemit(ev: DevToolsEvent): void {
    this.dev.pushEvent({
      source: ev.source,
      type: `${ev.type}#replay`,
      level: ev.level,
      demarcheRef: ev.demarcheRef,
      payload: ev.payload,
    });
  }

  exportNdjson(): void {
    try {
      const lines = this.filteredEvents()
        .map((e) => this.dev.serialize(e).replace(/\n\s*/g, ' '))
        .join('\n');
      const blob = new Blob([lines], { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devtools-events-${Date.now()}.ndjson`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      /* ignore */
    }
  }
}
